import axios from 'axios';

// Determine API URL based on environment
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const envApiUrl = import.meta.env.VITE_API_URL;
  
  // Check if we're in production (deployed)
  if (hostname === 'sphere.fun' || hostname === 'www.sphere.fun') {
    return 'https://api.sphere.fun';
  }
  
  // Use environment variable or fallback to localhost for development
  const apiUrl = envApiUrl || 'http://localhost:3000';
  return apiUrl;
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Helper function to validate JWT token format
const isValidJWT = (token: string): boolean => {
  if (!token) return false;
  
  // JWT tokens have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  // Each part should be base64url encoded (no padding)
  const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => base64UrlRegex.test(part));
};

// Helper function to clean invalid tokens
const cleanInvalidTokens = () => {
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (accessToken && !isValidJWT(accessToken)) {
    console.warn('Invalid accessToken found, removing...');
    localStorage.removeItem('accessToken');
  }
  
  if (token && !isValidJWT(token)) {
    console.warn('Invalid token found, removing...');
    localStorage.removeItem('token');
  }
  
  if (refreshToken && !isValidJWT(refreshToken)) {
    console.warn('Invalid refreshToken found, removing...');
    localStorage.removeItem('refreshToken');
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Clean any invalid tokens first
    cleanInvalidTokens();
    
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('refreshToken');
    
    if (token && isValidJWT(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (token) {
      // If we have a token but it's invalid, clear it
      console.warn('Invalid token found, clearing storage...');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token if we have a refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${getApiUrl()}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
          
          const newToken = response.data.data.token;
          localStorage.setItem('accessToken', newToken);
          localStorage.setItem('token', newToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          
          // Redirect to home page or login
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, clear storage and redirect
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        // Only redirect if we're not already on the home page
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Export function to manually clear all tokens (for debugging)
export const clearAllTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  console.log('🧹 All tokens cleared from localStorage');
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearAllTokens = clearAllTokens;
}

// Create a separate public API instance without auth headers
export const publicApi = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

export default api; 